import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  TextField,
  Autocomplete,
  Snackbar,
  Alert,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import moment from "moment";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { useGetPriceChangeReportQuery } from "store/services/reportApi";
import { useLazyGetDDLUserListQuery } from "store/services/userApi";
import HighlightedText from "components/Shared/HighlightedText";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addDays, differenceInDays } from "date-fns";


const PriceChangeReport = () => {
  const { isVendor, vendorId, viewEntity } = useAuth();
  const { reset } = useForm({ defaultValues: { selectedLogs: [] } });
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [sortBy] = useState("createdAt:-1");
  const [user, setUser] = useState("all");
  const [timeRange, setTimeRange] = useState("onemonth");
  const [customFromDate, setCustomFromDate] = useState(null);
  const [customToDate, setCustomToDate] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [
    { title: "Reports", path: "/app/crm/reports/list" },
    { title: "Price Change Report" },
  ];

  const [
    fetchUsers,
    { data: usersData, isLoading: isUsersLoading, error: usersError },
  ] = useLazyGetDDLUserListQuery();

  const {
    data: priceChangeData,
    isLoading,
    isFetching,
    error,
  } = useGetPriceChangeReportQuery({
    page: currentPage,
    limit: perPage,
    ...(keyword && { keyword }),
    ...(isVendor && { vendorId }),
    account: viewEntity?.entity?.account?._id,
    entity: viewEntity?.entity?._id,
    changedBy: user !== "all" ? user : undefined,
    dateRange: timeRange !== "all" ? timeRange.toLowerCase() : undefined,
    startDate:
      timeRange === "custom" && customFromDate
        ? moment(customFromDate).format("MM-DD-YYYY")
        : undefined,
    endDate:
      timeRange === "custom" && customToDate
        ? moment(customToDate).format("MM-DD-YYYY")
        : undefined,
    sortedBy: sortBy.split(":")[0],
    sortDirection: parseInt(sortBy.split(":")[1]),
  });

  useEffect(() => {
    console.log("API Response:", { priceChangeData, error, isLoading, isFetching });
    console.log("Auth Data:", { viewEntity, isVendor, vendorId });
    fetchUsers({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
    });
  }, [fetchUsers, viewEntity, priceChangeData, error, isLoading, isFetching]);

  const userOptions = [
    { label: "All Users", value: "all" },
    ...(usersData?.map((user) => ({
      label: user.label || user.name,
      value: user.value || user._id,
    })) || []),
  ];

  const getDateRange = useCallback((range, from, to) => {
    const today = moment().startOf("day");
    switch (range.toLowerCase()) {
      case "today":
        return {
          fromDate: today.format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "last7days":
        return {
          fromDate: moment()
            .subtract(6, "days")
            .startOf("day")
            .format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "onemonth":
        return {
          fromDate: moment()
            .subtract(1, "month")
            .startOf("day")
            .format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "custom":
        if (from && to && moment(from).isValid() && moment(to).isValid()) {
          if (differenceInDays(new Date(to), new Date(from)) > 90) {
            setErrorMessage("Date range cannot exceed 90 days.");
            return { fromDate: null, toDate: null };
          }
          return {
            fromDate: moment(from).format("MM-DD-YYYY"),
            toDate: moment(to).format("MM-DD-YYYY"),
          };
        }
        setErrorMessage(
          "Invalid date range. Ensure 'To' date is after 'From' date."
        );
        return { fromDate: null, toDate: null };
      case "all":
      default:
        return { fromDate: null, toDate: null };
    }
  }, []);

  const fetchReportData = useCallback(() => {
    const [sortedBy, sortDirection] = sortBy.split(":");
    const { fromDate, toDate } = getDateRange(
      timeRange,
      customFromDate,
      customToDate
    );

    const newParams = new URLSearchParams(searchParams);
    if (fromDate && timeRange !== "all") newParams.set("fromDate", fromDate);
    else newParams.delete("fromDate");
    if (toDate && timeRange !== "all") newParams.set("toDate", toDate);
    else newParams.delete("toDate");
    newParams.set("user", user);
    setSearchParams(newParams, { replace: true });
  }, [
    timeRange,
    user,
    customFromDate,
    customToDate,
    setSearchParams,
    searchParams,
    getDateRange,
    sortBy,
  ]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const clearAllFilters = () => {
    setSearchParams({});
    setTimeRange("all");
    setUser("all");
    setCustomFromDate(null);
    setCustomToDate(null);
    reset({ selectedLogs: [] });
  };

  const columns = [
    {
      name: "Shipment ID",
      selector: (row) => row?.documentId?.shipment?.shipmentId,
      cell: (row) => (
        <Link
          title={row?.documentId?.shipment?.shipmentId}
          to={`/app/crm/shipment/${row.documentId?.shipment?._id}/detail?tab=pricing`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.documentId?.shipment?.shipmentId ? (
            <HighlightedText
              text={row.documentId?.shipment?.shipmentId}
              highlight={keyword}
            />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Old Value",
      selector: (row) => row.oldValue,
      cell: (row) =>
        Array.isArray(row.oldValue) ? (
          <ul className="list-disc pl-4">
            {row.oldValue.map((item, index) =>
              Object.entries(item).map(([key, value]) => (
                <li key={`${key}-${index}`}>
                  <strong>{key}</strong>: {String(value)}
                </li>
              ))
            )}
          </ul>
        ) : (
          "N/A"
        ),
    },
    {
      name: "New Value",
      selector: (row) => row.newValue,
      cell: (row) =>
        Array.isArray(row.newValue) ? (
          <ul className="list-disc pl-4">
            {row.newValue.map((item, index) =>
              Object.entries(item).map(([key, value]) => (
                <li key={`${key}-${index}`}>
                  <strong>{key}</strong>: {String(value)}
                </li>
              ))
            )}
          </ul>
        ) : (
          "N/A"
        ),
    },
    {
      name: "Description",
      selector: (row) => row.description,
      cell: (row) => row.description || "N/A",
      grow: 2,
      wrap: true,
    },
    {
      name: "Changed By",
      selector: (row) => row.changedBy?.name,
      cell: (row) => row.changedBy?.name || "N/A",
    },
    {
      name: "Time",
      selector: (row) => row.createdAt,
      cell: (row) => <AppTableDateTime date={row.createdAt} />,
    },
  ];

  const filteredData = priceChangeData?.data?.details || [];

  useAppMeta("Price Change Report");

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <BreadcrumbLayout breadcrumbs={breadcrumbs}>
        <BreadcrumbLayout.Error error={error || usersError} />
        <BreadcrumbLayout.Paper>
          {/* TOP FILTER ROW (Range, Changed By) */}


         
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end", 
              gap: 2,
              px: 2,
              pt: 2,
            }}
          >
          
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
             
              <BaseSelect
                label="Range"
                value={timeRange}
                onChange={(e) => {
                  setTimeRange(e.target.value);
                  if (e.target.value !== "custom") {
                    setCustomFromDate(null);
                    setCustomToDate(null);
                  }
                }}
                options={[
                  { label: "Today", value: "today" },
                  { label: "Last 7 Days", value: "last7days" },
                  { label: "One Month", value: "onemonth" },
                  { label: "Custom", value: "custom" },
                  { label: "All", value: "all" },
                ]}
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.value}
                size="small"
                sx={{ minWidth: 140 }}
              />
              {timeRange === "custom" && (
                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                  <DatePicker
                    label="From"
                    value={customFromDate}
                    onChange={(newValue) => {
                      setCustomFromDate(newValue);
                      if (
                        customToDate &&
                        differenceInDays(customToDate, newValue) > 90
                      ) {
                        setCustomToDate(addDays(newValue, 90));
                        setErrorMessage("Date range adjusted to maximum 90 days.");
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ width: 140 }} />
                    )}
                    maxDate={customToDate || new Date()}
                    disableFuture
                  />
                  <DatePicker
                    label="To"
                    value={customToDate}
                    onChange={(newValue) => {
                      setCustomToDate(newValue);
                      if (
                        customFromDate &&
                        differenceInDays(newValue, customFromDate) > 90
                      ) {
                        setCustomFromDate(addDays(newValue, -90));
                        setErrorMessage("Date range adjusted to maximum 90 days.");
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ width: 140 }} />
                    )}
                    minDate={customFromDate}
                    maxDate={new Date()}
                    disableFuture
                  />
                </Box>
              )}
              <Autocomplete
                size="small"
                options={userOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                value={
                  userOptions.find((opt) => opt.value === user) || {
                    value: "all",
                    label: "All Users",
                  }
                }
                onChange={(e, newValue) => setUser(newValue?.value || "all")}
                disableClearable
                disabled={isUsersLoading || isLoading || isFetching}
                sx={{ minWidth: 200 }}
                renderInput={(params) => (
                  <TextField {...params} label="Changed By" />
                )}
              />

              <TableFilter
                searchLabel="Shipment ID"
                disabled={isLoading || isFetching || isUsersLoading}
                initialFilters={0}
                filterAlignment="right"
              />
            </Box>


          </Box>


          {/* TABLE */}
          {isLoading || isFetching || isUsersLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="error">
                {error?.data?.message || "Failed to load data. Please try again."}
              </Typography>
            </Box>
          ) : filteredData.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No price update logs available for the selected filters. Try adjusting the
                filters or check if update logs exist in the database.
              </Typography>
            </Box>
          ) : (
            <AppTable
              columns={columns}
              data={filteredData}
              progressPending={isLoading || isFetching}
              paginationServer
              paginationPerPage={perPage}
              paginationTotalRows={priceChangeData?.data?.totalItems ?? 0}
              paginationDefaultPage={currentPage}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
            />
          )}

          {/* ERROR SNACKBAR */}
          <Snackbar
            open={!!errorMessage}
            autoHideDuration={6000}
            onClose={() => setErrorMessage("")}
          >
            <Alert severity="error" onClose={() => setErrorMessage("")}>
              {errorMessage}
            </Alert>
          </Snackbar>
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </LocalizationProvider>
  );
};

export default PriceChangeReport;